/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {Device} from '@ion/core/src/types/entities';
import {DeviceEditor} from '@ion/dls/src/editors/DeviceEditor';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {addAdminDevice, addDevice} from './state';

export interface AddDeviceEditorProps {
  onSwitchToBulkAdd?: (e: MouseEvent<HTMLButtonElement, MouseEvent>, id?: number) => void;
  includeGroupProperties?: boolean;
  disableAccountChooser: boolean;
  showSCISettings: boolean;
  enableBulkAdd?: boolean;
  defaultLabel?: boolean;
  isViewDialog: boolean;
  isCtcAdmin: boolean;
};

/**
 * Display Add Device Editor
 */
export const AddDeviceEditor = ({
  onSwitchToBulkAdd,
  isCtcAdmin,
  isViewDialog,
  enableBulkAdd,
  defaultLabel= false,
  showSCISettings = false,
  disableAccountChooser = false,
  includeGroupProperties = true
}: AddDeviceEditorProps) => {
  const dispatch                   = useDispatch();
  const {globalGroup}              = useSelector(({globalGroup}) => globalGroup);
  const {account}                  = useAuthenticatedUser();
  const {result: created, loading} = useSelector(({devices: {add}}) => add);
  const [clearForm, setClearForm]  = useState(false);
  const systemOfUnits              = useSystemOfUnits();
  const isView                     = isViewDialog || false;

  useEffect(() => {
    if (created) {
      setClearForm(true);
      dispatch(addAdminDevice(false));
    }
  }, [created]);

  function addNewDevice(device: Device) {
    dispatch(addDevice(device, true));
  }

  function onCancel() {
    dispatch(addAdminDevice(false));
  }

  return (
    <DeviceEditor open
      includeGPSFields={!isCtcAdmin}
      includeAdditionalDeviceFields={isCtcAdmin}
      isDeviceNotes={isCtcAdmin}
      includeDatetimeInformation={isCtcAdmin}
      includeGroupProperties={includeGroupProperties}
      showSCISettings={showSCISettings}
      disableAccountChooser={disableAccountChooser}
      isViewDialog={isView}
      enableBulkAdd={enableBulkAdd}
      onSwitchToBulkAdd={onSwitchToBulkAdd}
      account={globalGroup || account}
      onSubmit={addNewDevice}
      onCloseDialog={onCancel}
      clear={clearForm}
      systemOfUnits={systemOfUnits}
      loading={loading}
      defaultLabel={defaultLabel} />
  );
};

export default memo(AddDeviceEditor);
